import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Autocomplete, Button, CircularProgress, Collapse, FormControlLabel,
  Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem,
  Switch, TextField,
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SearchIcon from '@mui/icons-material/Search';
import { toast } from 'react-toastify';
import { useAuth } from '../../hook';
import {
  assignSalePayments, createPayment, getAcquisitionChannels, getClientSales, getSale,
  getServices, regularizePaymentDetraction, updatePayment, updateSale,
} from './clientApi';
import { ClientConfirmDialog } from './ClientConfirmDialog';
import './client.scss';

const PAGE_SIZE = 10;
const today = () => new Date().toISOString().slice(0, 10);
const money = (value, currency) => `${currency === 'USD' ? 'USD' : 'S/'} ${Number(value || 0).toFixed(2)}`;
const readableDate = (value) => {
  if (!value) return 'Sin fecha';
  const [year, month, day] = value.split('-').map(Number);
  return new Intl.DateTimeFormat('es-PE', {
    day: '2-digit', month: 'short', year: 'numeric',
  }).format(new Date(year, month - 1, day));
};
const monthKey = (value) => `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}`;
const monthName = (value) => new Intl.DateTimeFormat('es-PE', {
  month: 'long', year: 'numeric',
}).format(value);
const paymentBreakdown = (sale, amount) => {
  const total = Number(amount || 0);
  const detraction = sale?.detraction_applied ? total * Number(sale.detraction_rate || 0) / 100 : 0;
  return {
    total,
    detraction,
    detractionPen: sale?.currency === 'USD' ? detraction * Number(sale.exchange_rate || 0) : detraction,
    agencyDeposit: total - detraction,
  };
};

export function UpdateSale() {
  const navigate = useNavigate();
  const { auth } = useAuth();
  const [listData, setListData] = useState({ count: 0, next: null, previous: null, results: [] });
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [submittedSearch, setSubmittedSearch] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [monthCursor, setMonthCursor] = useState(() => new Date());
  const [refreshKey, setRefreshKey] = useState(0);
  const [sale, setSale] = useState(null);
  const [channels, setChannels] = useState([]);
  const [serviceOptions, setServiceOptions] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [primaryService, setPrimaryService] = useState(null);
  const [acquisitionChannel, setAcquisitionChannel] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [commercialTerms, setCommercialTerms] = useState('');
  const [moreFieldsOpen, setMoreFieldsOpen] = useState(false);
  const [extraFields, setExtraFields] = useState({
    description: '', currency: 'PEN', exchange_rate: '1', detraction_applied: false,
    detraction_rate: '12',
  });
  const [paymentAssignments, setPaymentAssignments] = useState({});
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState('');
  const [confirmation, setConfirmation] = useState(null);
  const [legacyPayment, setLegacyPayment] = useState(null);
  const [legacyAmounts, setLegacyAmounts] = useState({
    bank_deposit: '', detraction_amount: '',
  });
  const totalPages = Math.max(1, Math.ceil(listData.count / PAGE_SIZE));
  const listMode = selectedMonth ? 'month' : (submittedSearch ? 'search' : 'pending');

  useEffect(() => {
    getAcquisitionChannels(auth.token).then(setChannels).catch((err) => setError(err.message));
    getServices(auth.token).then(setServiceOptions).catch((err) => setError(err.message));
  }, [auth.token]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    const params = selectedMonth
      ? { month: selectedMonth }
      : { page, page_size: PAGE_SIZE, ...(submittedSearch ? { search: submittedSearch } : {}) };
    getClientSales(auth.token, params)
      .then((data) => { if (active) setListData(data); })
      .catch((err) => { if (active) setError(err.message); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [auth.token, page, refreshKey, selectedMonth, submittedSearch]);

  useEffect(() => {
    if (!sale) {
      setDrafts({});
      setCommercialTerms('');
      setAcquisitionChannel(null);
      setPaymentAssignments({});
      setSelectedServices([]);
      setPrimaryService(null);
      setMoreFieldsOpen(false);
      return;
    }
    setCommercialTerms((sale.payment_plan || []).map((item) => item.percentage).join(', '));
    setAcquisitionChannel(sale.acquisition_channel || null);
    setSelectedServices(sale.services || []);
    setPrimaryService(sale.primary_service || sale.services?.[0] || null);
    setServiceOptions((previous) => {
      const merged = [...previous, ...(sale.services || [])];
      return [...new Map(merged.map((service) => [service.id, service])).values()];
    });
    setExtraFields({
      description: sale.description || '',
      currency: sale.currency || 'PEN',
      exchange_rate: sale.exchange_rate || '1',
      detraction_applied: Boolean(sale.detraction_applied),
      detraction_rate: sale.detraction_rate || '12',
    });
    setPaymentAssignments(Object.fromEntries(
      (sale.payments || [])
        .filter((payment) => payment.installment_number == null)
        .map((payment) => [payment.id, '']),
    ));
    const next = {};
    (sale.installments || []).forEach((installment) => {
      next[installment.number] = {
        date: installment.due_date || today(),
        amount: installment.pending_amount,
        paymentId: null,
      };
    });
    setDrafts(next);
  }, [sale]);

  const submitSearch = (event) => {
    event?.preventDefault();
    const value = searchInput.trim();
    setSelectedMonth('');
    setPage(1);
    setSubmittedSearch(value);
  };

  const changeMonth = (value) => {
    setSelectedMonth(value);
    setSearchInput('');
    setSubmittedSearch('');
    setPage(1);
  };

  const moveMonth = (amount) => {
    const nextMonth = new Date(
      monthCursor.getFullYear(), monthCursor.getMonth() + amount, 1,
    );
    setMonthCursor(nextMonth);
    changeMonth(monthKey(nextMonth));
  };

  const clearMonth = () => {
    setMonthCursor(new Date());
    changeMonth('');
  };

  const openSale = async (id) => {
    setDetailLoading(true);
    setError('');
    try {
      setSale(await getSale(auth.token, id));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(err.message);
    } finally {
      setDetailLoading(false);
    }
  };

  const refreshDetail = async () => {
    setSale(await getSale(auth.token, sale.id));
    setRefreshKey((value) => value + 1);
  };

  const saveCommercialTerms = async () => {
    if (!acquisitionChannel?.id) return setError('Selecciona el canal de adquisición.');
    const percentages = commercialTerms.split(',')
      .map((item) => Number(item.trim()))
      .filter((item) => Number.isFinite(item) && item > 0);
    const total = percentages.reduce((sum, item) => sum + item, 0);
    if (!percentages.length || Math.abs(total - 100) > 0.001) {
      return setError('Las condiciones comerciales deben contener porcentajes positivos que sumen 100.');
    }
    setDetailLoading(true); setError('');
    try {
      await updateSale(auth.token, sale.id, {
        acquisition_channel_id: acquisitionChannel.id,
        payment_plan: percentages.map((percentage, index) => ({
          percentage: String(percentage),
          due_date: sale.payment_plan?.[index]?.due_date || null,
        })),
      });
      await refreshDetail();
      toast.success('Datos comerciales actualizados.');
    } catch (err) {
      setError(err?.data ? JSON.stringify(err.data) : err.message);
      toast.error('No se pudieron actualizar los datos comerciales.');
    } finally { setDetailLoading(false); }
  };

  const saveExtraFields = async () => {
    if (extraFields.currency === 'USD' && !(Number(extraFields.exchange_rate) > 0)) {
      return setError('Ingresa un tipo de cambio mayor que cero.');
    }
    if (
      extraFields.detraction_applied
      && (!(Number(extraFields.detraction_rate) > 0) || Number(extraFields.detraction_rate) > 100)
    ) {
      return setError('La detracción debe ser mayor que cero y menor o igual a 100.');
    }
    if (extraFields.detraction_applied && !sale.invoiced) {
      return setError('La detracción solo puede aplicarse a una venta con IGV.');
    }
    if (
      primaryService
      && !selectedServices.some((service) => service.id === primaryService.id)
    ) {
      return setError('El servicio principal debe estar entre los servicios seleccionados.');
    }
    setDetailLoading(true); setError('');
    try {
      await updateSale(auth.token, sale.id, {
        description: extraFields.description.trim(),
        currency: extraFields.currency,
        exchange_rate: extraFields.currency === 'PEN' ? '1.0000' : extraFields.exchange_rate,
        detraction_applied: extraFields.detraction_applied,
        detraction_rate: extraFields.detraction_rate,
        service_ids: selectedServices.map((service) => service.id),
        primary_service_id: primaryService?.id || null,
      });
      await refreshDetail();
      toast.success('Campos adicionales actualizados.');
    } catch (err) {
      setError(err?.data ? JSON.stringify(err.data) : err.message);
      toast.error('No se pudieron actualizar los campos adicionales.');
    } finally { setDetailLoading(false); }
  };

  const saveInstallment = async (installment) => {
    const draft = drafts[installment.number];
    if (!draft?.date || !(Number(draft.amount) > 0)) return setError('Completa fecha y monto del pago.');
    setDetailLoading(true); setError('');
    try {
      const payload = { sale: sale.id, installment_number: installment.number, date: draft.date, amount: draft.amount };
      if (draft.paymentId) await updatePayment(auth.token, draft.paymentId, payload);
      else await createPayment(auth.token, payload);
      await refreshDetail();
      toast.success(draft.paymentId ? 'Pago actualizado correctamente.' : 'Pago registrado correctamente.');
      setConfirmation(null);
    } catch (err) {
      setError(err?.data ? JSON.stringify(err.data) : err.message);
      toast.error('No se pudo guardar el pago.');
    } finally { setDetailLoading(false); }
  };

  const editPayment = (installment, payment) => {
    setDrafts((previous) => ({
      ...previous,
      [installment.number]: {
        date: payment.date,
        amount: payment.amount,
        paymentId: payment.id,
      },
    }));
  };

  const cancelPaymentEdit = (installment) => {
    setDrafts((previous) => ({
      ...previous,
      [installment.number]: {
        date: installment.due_date || today(),
        amount: installment.pending_amount,
        paymentId: null,
      },
    }));
  };

  const suggestAssignments = () => {
    const remaining = Object.fromEntries(
      (sale.installments || []).map((installment) => [
        installment.number,
        Math.max(Number(installment.planned_amount) - Number(installment.paid_amount), 0),
      ]),
    );
    const suggestions = {};
    [...(sale.payments || [])]
      .filter((payment) => payment.installment_number == null)
      .sort((first, second) => first.date.localeCompare(second.date) || first.id - second.id)
      .forEach((payment) => {
        const amount = Number(payment.amount);
        const match = (sale.installments || []).find(
          (installment) => amount <= remaining[installment.number] + 0.001,
        );
        suggestions[payment.id] = match?.number || '';
        if (match) remaining[match.number] -= amount;
      });
    setPaymentAssignments(suggestions);
  };

  const saveAssignments = async () => {
    const assignments = Object.entries(paymentAssignments)
      .filter(([, installment]) => installment !== '')
      .map(([paymentId, installment]) => ({
        payment_id: Number(paymentId),
        installment_number: Number(installment),
      }));
    if (!assignments.length) return setError('Selecciona al menos una cuota para guardar.');
    setDetailLoading(true); setError('');
    try {
      const updatedSale = await assignSalePayments(auth.token, sale.id, assignments);
      setSale(updatedSale);
      setRefreshKey((value) => value + 1);
      toast.success('Pagos históricos asignados correctamente.');
    } catch (err) {
      setError(err?.data ? JSON.stringify(err.data) : err.message);
      toast.error('No se pudieron asignar los pagos.');
    } finally { setDetailLoading(false); }
  };

  const openLegacyRegularization = (installment, payment) => {
    const rate = Number(sale.detraction_rate || 0);
    const bankDeposit = Number(payment.amount || 0);
    const suggestedDetraction = rate > 0 && rate < 100
      ? bankDeposit * rate / (100 - rate)
      : Math.max(Number(installment.pending_amount || 0), 0);
    setLegacyPayment({ installment, payment });
    setLegacyAmounts({
      bank_deposit: bankDeposit.toFixed(2),
      detraction_amount: suggestedDetraction.toFixed(2),
    });
  };

  const saveLegacyRegularization = async () => {
    if (
      !(Number(legacyAmounts.bank_deposit) > 0)
      || !(Number(legacyAmounts.detraction_amount) > 0)
    ) {
      return setError('El depósito y la detracción deben ser mayores que cero.');
    }
    setDetailLoading(true); setError('');
    try {
      await regularizePaymentDetraction(
        auth.token, legacyPayment.payment.id, legacyAmounts,
      );
      setLegacyPayment(null);
      await refreshDetail();
      toast.success('Pago histórico regularizado correctamente.');
    } catch (err) {
      setError(err?.data ? JSON.stringify(err.data) : err.message);
      toast.error('No se pudo regularizar el pago histórico.');
    } finally { setDetailLoading(false); }
  };

  const setDraft = (number, field, value) => setDrafts((previous) => ({
    ...previous,
    [number]: { ...previous[number], [field]: value },
  }));

  const header = (
    <header className="client-header client-update-header">
      <IconButton onClick={() => sale ? setSale(null) : navigate('/')}><ArrowBackIosNewIcon /></IconButton>
      <h1>Actualizar venta</h1><span />
    </header>
  );

  if (!sale) {
    return (
      <main className="client-page client-update-sale-page client-sales-browser">
        {header}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Button className="client-legacy-entry" onClick={() => navigate('/client/regularizar-ventas')}>
          Regularizar ventas antiguas
        </Button>
        <form className="client-sales-search" onSubmit={submitSearch}>
          <TextField fullWidth value={searchInput} onChange={(event) => setSearchInput(event.target.value)}
            placeholder="Buscar por nombre de cliente" inputProps={{ 'aria-label': 'Buscar ventas' }} />
          <IconButton type="submit" aria-label="Buscar"><SearchIcon /></IconButton>
        </form>
        <p className="client-sales-mode">
          {listMode === 'pending' && 'Últimas ventas pendientes de pago'}
          {listMode === 'search' && `Resultados para “${submittedSearch}”`}
          {listMode === 'month' && `Ventas de ${monthName(monthCursor)}`}
        </p>

        {loading || detailLoading ? <div className="client-empty"><CircularProgress size={28} /></div> : (
          <div className="client-sale-card-list">
            {!listData.results.length && <div className="client-empty">No se encontraron ventas.</div>}
            {listData.results.map((item) => (
              <button type="button" className={`client-sale-card${item.is_paid ? ' client-sale-card--paid' : ''}`}
                key={item.id} onClick={() => openSale(item.id)}>
                <div className="client-sale-card__main">
                  <strong>{item.client_name || 'Sin cliente'}</strong>
                  <span>{readableDate(item.issue_date)}</span>
                </div>
                <div className="client-sale-card__amounts">
                  <span>({money(item.total, item.currency)})</span>
                  <strong>{item.is_paid ? money(item.total, item.currency) : money(item.pending_amount, item.currency)}</strong>
                  <em>{item.is_paid ? 'Completada' : `Pagos ${item.paid_installments}/${item.installments_count || 1}`}</em>
                </div>
              </button>
            ))}
          </div>
        )}

        {listMode !== 'month' && totalPages > 1 && (
          <div className="client-sales-pagination">
            <Button disabled={!listData.previous} onClick={() => setPage((value) => value - 1)}>Anterior</Button>
            <span>{page} de {totalPages}</span>
            <Button disabled={!listData.next} onClick={() => setPage((value) => value + 1)}>Siguiente</Button>
          </div>
        )}

        <div className="client-sales-month-filter">
          <span className="client-sales-month-filter__label">Filtrar por mes</span>
          <div className="client-month-picker">
            <IconButton onClick={() => moveMonth(-1)} aria-label="Mes anterior"><ChevronLeftIcon /></IconButton>
            <strong>{monthName(monthCursor)}</strong>
            <IconButton onClick={() => moveMonth(1)} aria-label="Mes siguiente"><ChevronRightIcon /></IconButton>
          </div>
          {selectedMonth && <Button onClick={clearMonth}>Borrar filtro de fecha</Button>}
        </div>
      </main>
    );
  }

  return (
    <main className="client-page client-update-sale-page">
      {header}
      {error && <Alert severity="error">{error}</Alert>}
      <div className="client-form">
        <div className="client-commercial-terms">
          <Autocomplete options={channels} value={acquisitionChannel}
            getOptionLabel={(option) => option?.name || ''}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
            onChange={(_, value) => setAcquisitionChannel(value)}
            renderInput={(params) => <TextField {...params} label="Canal de adquisición" />} />
          <TextField fullWidth label="Condiciones comerciales" value={commercialTerms}
            onChange={(event) => setCommercialTerms(event.target.value)}
            helperText="Porcentajes separados por coma. Ejemplo: 50, 50" />
          <Button variant="outlined" onClick={saveCommercialTerms} disabled={detailLoading}>Guardar cambios comerciales</Button>
          {(sale.payments || []).some((payment) => payment.installment_number == null) && (
            <Alert severity="warning">Esta venta tiene pagos históricos sin cuota asignada.</Alert>
          )}
        </div>
        <Button className="client-more-fields-toggle" variant="text"
          onClick={() => setMoreFieldsOpen((value) => !value)}>
          {moreFieldsOpen ? 'Ocultar campos adicionales' : 'Editar más campos'}
        </Button>
        <Collapse in={moreFieldsOpen}>
          <section className="client-extra-sale-fields">
            <div className="client-extra-sale-fields__heading">
              <strong>Campos adicionales</strong>
              <small>Actualiza información general e impuestos de esta venta.</small>
            </div>
            <TextField label="Descripción" multiline minRows={3}
              value={extraFields.description}
              onChange={(event) => setExtraFields((previous) => ({
                ...previous, description: event.target.value,
              }))} />
            <Autocomplete multiple options={serviceOptions} value={selectedServices}
              filterSelectedOptions
              getOptionLabel={(option) => option?.name || ''}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              onInputChange={(_, value, reason) => {
                if (reason !== 'input') return;
                const query = value.trim();
                if (query.length < 3) return;
                getServices(auth.token, query).then((results) => {
                  setServiceOptions((previous) => {
                    const merged = [...results, ...previous, ...selectedServices];
                    return [...new Map(merged.map((service) => [service.id, service])).values()];
                  });
                }).catch((err) => setError(err.message));
              }}
              onChange={(_, values) => {
                setSelectedServices(values);
                if (primaryService && !values.some((service) => service.id === primaryService.id)) {
                  setPrimaryService(values[0] || null);
                }
              }}
              renderInput={(params) => <TextField {...params}
                label="Servicios contratados" placeholder="Buscar y seleccionar" />} />
            <Autocomplete options={selectedServices} value={primaryService}
              disabled={!selectedServices.length}
              getOptionLabel={(option) => option?.name || ''}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              onChange={(_, value) => setPrimaryService(value)}
              renderInput={(params) => <TextField {...params}
                label="Servicio principal"
                helperText="Debe ser uno de los servicios contratados." />} />
            <div className="client-row">
              <TextField select label="Moneda" value={extraFields.currency}
                disabled={(sale.payments || []).length > 0}
                helperText={(sale.payments || []).length ? 'Bloqueada porque la venta ya tiene pagos.' : ''}
                onChange={(event) => setExtraFields((previous) => ({
                  ...previous, currency: event.target.value,
                }))}>
                <MenuItem value="PEN">Soles (PEN)</MenuItem>
                <MenuItem value="USD">Dólares (USD)</MenuItem>
              </TextField>
              {extraFields.currency === 'USD' && <TextField label="Tipo de cambio" type="number"
                value={extraFields.exchange_rate} inputProps={{ min: '0.0001', step: '0.0001' }}
                onChange={(event) => setExtraFields((previous) => ({
                  ...previous, exchange_rate: event.target.value,
                }))} />}
            </div>
            <div className="client-extra-sale-fields__detraction">
              <FormControlLabel label="Aplicar detracción"
                control={<Switch checked={extraFields.detraction_applied}
                  disabled={!sale.invoiced}
                  onChange={(event) => setExtraFields((previous) => ({
                    ...previous, detraction_applied: event.target.checked,
                  }))} />} />
              {extraFields.detraction_applied && <TextField label="Tasa de detracción (%)"
                type="number" value={extraFields.detraction_rate}
                inputProps={{ min: '0.01', max: '100', step: '0.01' }}
                onChange={(event) => setExtraFields((previous) => ({
                  ...previous, detraction_rate: event.target.value,
                }))} />}
              {!sale.invoiced && <small>Esta venta no tiene IGV; por eso no puede aplicar detracción.</small>}
            </div>
            <Button variant="contained" className="client-primary"
              disabled={detailLoading} onClick={saveExtraFields}>
              Guardar campos adicionales
            </Button>
          </section>
        </Collapse>

        {(sale.payments || []).some((payment) => payment.installment_number == null) && (
          <section className="client-payment-reconciliation">
            <div className="client-payment-reconciliation__heading">
              <div><strong>Pagos pendientes de asignación</strong><small>Asigna los pagos históricos sin modificar sus montos o fechas.</small></div>
              <Button onClick={suggestAssignments}>Sugerir</Button>
            </div>
            <div className="client-payment-reconciliation__list">
              {(sale.payments || []).filter((payment) => payment.installment_number == null)
                .sort((first, second) => first.date.localeCompare(second.date) || first.id - second.id)
                .map((payment) => <div key={payment.id}>
                  <div><strong>{money(payment.amount, payment.currency)}</strong><small>{readableDate(payment.date)}{payment.operation_number ? ` · Op. ${payment.operation_number}` : ''}</small></div>
                  <TextField select size="small" label="Asignar a" value={paymentAssignments[payment.id] || ''}
                    onChange={(event) => setPaymentAssignments((previous) => ({ ...previous, [payment.id]: event.target.value }))}>
                    <MenuItem value="">Sin asignar</MenuItem>
                    {(sale.installments || []).map((installment) => <MenuItem key={installment.number} value={installment.number}>
                      Cuota {installment.number} · pendiente {money(installment.pending_amount, sale.currency)}
                    </MenuItem>)}
                  </TextField>
                </div>)}
            </div>
            <Button variant="contained" onClick={saveAssignments} disabled={detailLoading}>Guardar asignaciones</Button>
          </section>
        )}

        {detailLoading && <CircularProgress size={24} />}
        {(sale.installments || []).map((installment) => {
          const draft = drafts[installment.number] || {};
          const linkedPayments = (sale.payments || []).filter(
            (payment) => payment.installment_number === installment.number,
          );
          const tax = sale.invoiced ? Number(draft.amount || 0) * 18 / 118 : 0;
          const breakdown = paymentBreakdown(sale, draft.amount);
          const paid = installment.status === 'paid';
          return (
            <section className="client-installment" key={installment.number}>
              <div className="client-installment__heading">
                <div><strong>Cuota {installment.number}</strong><small>{installment.percentage}% · Planificado {money(installment.planned_amount, sale.currency)}</small></div>
                <span className={`client-installment__status client-installment__status--${paid ? 'paid' : 'pending'}`}>
                  {paid ? 'Pagada' : installment.status === 'partial' ? 'Parcial' : 'Pendiente'}
                </span>
              </div>
              {linkedPayments.length > 0 && <div className="client-installment-payment-list">
                {linkedPayments.map((payment) => <div key={payment.id}>
                  <div><strong>{money(payment.amount, payment.currency)}</strong><small>{readableDate(payment.date)}{payment.operation_number ? ` · Op. ${payment.operation_number}` : ''}</small></div>
                  <div className="client-installment-payment-actions">
                    {sale.detraction_applied && (!payment.detraction_applied || Number(payment.detraction_amount) === 0) && (
                      <Button className="client-regularize-button"
                        onClick={() => openLegacyRegularization(installment, payment)}>
                        Regularizar detracción
                      </Button>
                    )}
                    <Button onClick={() => editPayment(installment, payment)}>Editar</Button>
                  </div>
                </div>)}
              </div>}
              {(!paid || draft.paymentId) && <>
                <div className="client-row client-installment-editor">
                  <TextField label="Fecha de pago" type="date" value={draft.date || ''}
                    onChange={(event) => setDraft(installment.number, 'date', event.target.value)} InputLabelProps={{ shrink: true }} />
                  <TextField label={draft.paymentId ? 'Monto del pago' : 'Nuevo pago'} type="number"
                    value={draft.amount || ''} onChange={(event) => setDraft(installment.number, 'amount', event.target.value)} />
                </div>
                <div className="client-summary" style={{ marginTop: 12 }}>
                  <Value label="IGV" value={money(tax, sale.currency)} />
                  <Value label="Detracción" value={sale.currency === 'USD'
                    ? `${money(breakdown.detraction, 'USD')} · ${money(breakdown.detractionPen, 'PEN')}`
                    : money(breakdown.detraction, 'PEN')} />
                  <Value label="Depósito a la agencia" value={money(breakdown.agencyDeposit, sale.currency)} />
                </div>
              </>}
              <div className="client-installment-progress">
                <div><span style={{ width: `${Math.min(Number(installment.paid_amount) / Math.max(Number(installment.planned_amount), 1) * 100, 100)}%` }} /></div>
                <small>Pagado {money(installment.paid_amount, sale.currency)} · Pendiente {money(installment.pending_amount, sale.currency)}</small>
              </div>
              <div className="client-installment__footer">
                <span>{linkedPayments.length} pago{linkedPayments.length === 1 ? '' : 's'} asociado{linkedPayments.length === 1 ? '' : 's'}</span>
                <div>
                  {draft.paymentId && <Button onClick={() => cancelPaymentEdit(installment)}>Cancelar edición</Button>}
                  {(!paid || draft.paymentId) && <Button variant="contained" className="client-primary" onClick={() => setConfirmation(installment)} disabled={detailLoading}>
                    {draft.paymentId ? 'Guardar modificación' : 'Registrar pago'}
                  </Button>}
                </div>
              </div>
            </section>
          );
        })}
      </div>

      <section className="client-sale-details client-sale-details--expanded">
        <div className="client-sale-details__header">
          <div><small>Detalles de la venta</small><strong>{sale.client?.name || 'Sin cliente'}</strong></div>
          <span className={`client-sale-status client-sale-status--${sale.status}`}>{sale.status === 'paid' ? 'Pagada' : sale.status === 'invoiced' ? 'Facturada' : sale.status === 'canceled' ? 'Cancelada' : 'Abierta'}</span>
        </div>
        <div className="client-sale-progress">
          <div><span style={{ width: `${Math.min(Number(sale.paid_amount) / Math.max(Number(sale.total), 1) * 100, 100)}%` }} /></div>
          <small>{(Number(sale.paid_amount) / Math.max(Number(sale.total), 1) * 100).toFixed(0)}% cobrado</small>
        </div>
        <div className="client-sale-financial-grid">
          <Value label="Subtotal" value={money(sale.amount_without_igv, sale.currency)} />
          <Value label="IGV" value={money(sale.igv, sale.currency)} />
          <Value label="Total" value={money(sale.total, sale.currency)} />
          <Value label="Pagado" value={money(sale.paid_amount, sale.currency)} />
          <Value label="Pendiente" value={money(sale.pending_amount, sale.currency)} />
          <Value label="Moneda" value={sale.currency === 'USD' ? `USD · TC ${sale.exchange_rate}` : 'Soles (PEN)'} />
        </div>
        <div className="client-sale-information-grid">
          <div><span>Fecha de venta</span><strong>{readableDate(sale.issue_date)}</strong></div>
          <div><span>Canal de adquisición</span><strong>{sale.acquisition_channel?.name || 'Sin canal'}</strong></div>
          <div><span>Servicio principal</span><strong>{sale.primary_service?.name || 'Sin servicio principal'}</strong></div>
          <div><span>Servicios contratados</span><strong>{(sale.services || []).map((service) => service.name).join(', ') || 'Sin servicios'}</strong></div>
          <div><span>Condiciones comerciales</span><strong>{(sale.payment_plan || []).map((item) => `${item.percentage}%`).join(' · ') || 'Sin plan'}</strong></div>
          <div><span>Impuestos</span><strong>{sale.invoiced ? `Con IGV${sale.detraction_applied ? ` · Detracción ${sale.detraction_rate}%` : ''}` : 'Sin IGV'}</strong></div>
        </div>
      </section>
      <ClientConfirmDialog open={Boolean(confirmation)}
        title={drafts[confirmation?.number]?.paymentId ? '¿Modificar este pago?' : '¿Registrar este pago?'}
        message={confirmation ? (() => {
          const breakdown = paymentBreakdown(sale, drafts[confirmation.number]?.amount);
          return `Se aplicarán ${money(breakdown.total, sale.currency)} a la deuda: ${money(breakdown.agencyDeposit, sale.currency)} depositados y ${money(breakdown.detraction, sale.currency)} destinados a detracciones.`;
        })() : ''}
        confirmText={drafts[confirmation?.number]?.paymentId ? 'Sí, modificar' : 'Sí, registrar'}
        loading={detailLoading} onCancel={() => setConfirmation(null)} onConfirm={() => saveInstallment(confirmation)} />
      <Dialog className="client-expense-dialog client-regularize-dialog"
        open={Boolean(legacyPayment)}
        onClose={detailLoading ? undefined : () => setLegacyPayment(null)}
        fullWidth maxWidth="xs">
        <DialogTitle>Regularizar pago histórico</DialogTitle>
        <DialogContent className="client-expense-dialog__form">
          <div className="client-regularize-explanation">
            <strong>Cuota {legacyPayment?.installment.number}</strong>
            <span>Completa cómo se distribuyó el pago original.</span>
            <small>No se creará otro pago. Se actualizará el movimiento existente.</small>
          </div>
          <TextField label={`Depósito a la agencia (${sale.currency})`} type="number"
            value={legacyAmounts.bank_deposit}
            inputProps={{ min: '0.01', step: '0.01' }}
            onChange={(event) => setLegacyAmounts((previous) => ({
              ...previous, bank_deposit: event.target.value,
            }))} />
          <TextField label={`Detracción pagada (${sale.currency})`} type="number"
            value={legacyAmounts.detraction_amount}
            inputProps={{ min: '0.01', step: '0.01' }}
            onChange={(event) => setLegacyAmounts((previous) => ({
              ...previous, detraction_amount: event.target.value,
            }))} />
          <div className="client-regularize-total">
            <span>Total cancelado</span>
            <strong>{money(
              Number(legacyAmounts.bank_deposit) + Number(legacyAmounts.detraction_amount),
              sale.currency,
            )}</strong>
          </div>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button color="inherit" disabled={detailLoading}
            onClick={() => setLegacyPayment(null)}>Cancelar</Button>
          <Button variant="contained" disabled={detailLoading}
            onClick={saveLegacyRegularization}>
            {detailLoading ? 'Guardando...' : 'Regularizar pago'}
          </Button>
        </DialogActions>
      </Dialog>
    </main>
  );
}

function Value({ label, value }) {
  return <div className="client-value"><span>{label}</span><strong>{value}</strong></div>;
}
